import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiConfig } from '../shared/api-config';
import { AuthorizedUserApi } from '@shared/sdk-v1/apis/AuthorizedUserApi';
import { AuthorizedUser } from '@shared/sdk-v1/models/AuthorizedUser';
import { RootState } from '../store';

// Thunks for async actions
export const fetchAuthorizedUsers = createAsyncThunk(
  'authorizedUser/fetchAuthorizedUsers',
  async (courierUserId: number) => {
    const api = new AuthorizedUserApi(apiConfig());
    const response = await api.authorizedUserFind({
      filter: JSON.stringify({ where: { courierUserId } }),
    });
    return response;
  }
);

export const fetchAllAuthorizedUsers = createAsyncThunk(
  'authorizedUser/fetchAllAuthorizedUsers',
  async () => {
    const api = new AuthorizedUserApi(apiConfig());
    const response = await api.authorizedUserFind();
    console.log('All Authorized Users', response);
    return response;
  }
);

export const addAuthorizedUser = createAsyncThunk(
  'authorizedUser/addAuthorizedUser',
  async (userData: Omit<AuthorizedUser, 'id' | 'createdAt' | 'updatedAt'>) => {
    const api = new AuthorizedUserApi(apiConfig());
    const response = await api.authorizedUserCreate({ data: userData });
    return response;
  }
);

export const updateAuthorizedUser = createAsyncThunk(
  'authorizedUser/updateAuthorizedUser',
  async (userData: AuthorizedUser) => {
    const api = new AuthorizedUserApi(apiConfig());
    const { id, ...data } = userData;
    if (!id) throw new Error('User ID is required for updates.');
    const response = await api.authorizedUserPatchAttributes({
      id: id.toString(),
      data,
    });
    return response;
  }
);

export const deleteAuthorizedUser = createAsyncThunk(
  'authorizedUser/deleteAuthorizedUser',
  async (userId: number) => {
    const api = new AuthorizedUserApi(apiConfig());
    await api.authorizedUserDeleteById({ id: userId.toString() });
    return userId; // Return the ID to remove it from the state
  }
);

// State interface
interface AuthorizedUserState {
  isLoading: boolean;
  users: AuthorizedUser[];
  error?: string;
}

// Initial state
const initialState: AuthorizedUserState = {
  isLoading: false,
  users: [],
  error: undefined,
};

// Slice definition
export const authorizedUserSlice = createSlice({
  name: 'authorizedUser',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchAuthorizedUsers.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        fetchAuthorizedUsers.fulfilled,
        (state, action: PayloadAction<AuthorizedUser[]>) => {
          state.isLoading = false;
          state.users = action.payload;
        }
      )
      .addCase(fetchAuthorizedUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllAuthorizedUsers.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        fetchAllAuthorizedUsers.fulfilled,
        (state, action: PayloadAction<AuthorizedUser[]>) => {
          state.isLoading = false;
          state.users = action.payload;
        }
      )
      .addCase(fetchAllAuthorizedUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Add user
      .addCase(addAuthorizedUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        addAuthorizedUser.fulfilled,
        (state, action: PayloadAction<AuthorizedUser>) => {
          state.isLoading = false;
          state.users.push(action.payload);
        }
      )
      .addCase(addAuthorizedUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Update user
      .addCase(updateAuthorizedUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        updateAuthorizedUser.fulfilled,
        (state, action: PayloadAction<AuthorizedUser>) => {
          state.isLoading = false;
          const index = state.users.findIndex(
            (user) => user.id === action.payload.id
          );
          if (index !== -1) {
            state.users[index] = action.payload;
          }
        }
      )
      .addCase(updateAuthorizedUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Delete user
      .addCase(deleteAuthorizedUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        deleteAuthorizedUser.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.isLoading = false;
          state.users = state.users.filter(
            (user) => user.id !== action.payload
          );
        }
      )
      .addCase(deleteAuthorizedUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

// Export actions and selectors
export const { clearError } = authorizedUserSlice.actions;

// Export reducer
export default authorizedUserSlice.reducer;

export const selectAuthorizedUserState = (state: RootState) =>
  state.authorizedUser;

export const getAuthorizedUsers = (state: RootState) =>
  selectAuthorizedUserState(state).users;

export const getIsAuthorizedUserLoading = (state: RootState) =>
  selectAuthorizedUserState(state).isLoading;

export const getAuthorizedUserError = (state: RootState) =>
  selectAuthorizedUserState(state).error;
